import { useContext, useMemo } from 'react';
import { type Theme, ThemeContext, ThemeProvider as EmotionThemeProvider } from '@emotion/react';

import { __DEV__ } from '@toolbox/utils';
import { defaultTheme } from './defaultTheme';

import type { ReactNode } from 'react';

export type ThemeProviderProps = {
  theme?: Theme | ((theme: Theme) => Theme);
  children: ReactNode;
};

export const ThemeProvider = ({
  theme: localTheme = defaultTheme,
  children,
}: ThemeProviderProps): JSX.Element => {
  const outerTheme = useTheme<Theme>();

  if (__DEV__) {
    if (outerTheme === null && typeof localTheme === 'function') {
      // eslint-disable-next-line no-console
      console.error(
        [
          'You are providing a theme function prop to the ThemeProvider component:',
          '<ThemeProvider theme={outerTheme => outerTheme} />',
          'However, no outer theme is present.',
          'Make sure a theme is already injected higher in the React tree ' +
            'or provide a theme object.',
        ].join('\n'),
      );
    }
  }

  const theme = useMemo(
    () => (outerTheme === null ? localTheme : mergeOuterLocalTheme(outerTheme, localTheme)),
    [localTheme, outerTheme],
  );

  return <EmotionThemeProvider theme={theme}>{children}</EmotionThemeProvider>;
};

export function useTheme<T extends Theme>(): T {
  const theme = useContext(ThemeContext) as T;
  if (!theme) {
    throw Error(
      'useTheme: `theme` is undefined. Seems you forgot to wrap your app in `<ThemeProvider />`',
    );
  }

  return theme;
}

/**
 * Merges two themes together
 */
function mergeOuterLocalTheme<T extends Theme>(
  outerTheme: T,
  localTheme: T | ((theme: T) => T),
): T {
  if (typeof localTheme === 'function') {
    const mergedTheme = localTheme(outerTheme);

    if (__DEV__) {
      if (!mergedTheme) {
        // eslint-disable-next-line no-console
        console.error(
          [
            'You should return an object from your theme function, i.e.',
            '<ThemeProvider theme={() => ({})} />',
          ].join('\n'),
        );
      }
    }

    return mergedTheme;
  }

  return { ...outerTheme, ...localTheme };
}
